import React, { useMemo } from 'react';
import * as echarts from 'echarts';
import './index.scss';
import { chartAllColors } from '@/pages/opinion/constants';
import EChartCommon, {
  getAxisPointer,
} from '@/pages/opinion/components/EChartCommon';
import {
  formatTimeXAxisText,
  getChartPosition,
} from '@/pages/opinion/utils/chart';
import { ServerTimeType } from '@/pages/opinion/components/FilterCommon/types';
import { SymbolType } from '@/pages/opinion/constants/chart';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import { getLocale, useIntl } from 'umi';
import { getPercentage } from '@op/utils';
import {
  dealwithEChartYFuncMap,
  dealwithValueFuncMap,
  indicatorsMap,
} from '../../../../constants';
import { IndicatorsType } from '../../../../context';

type EChartsOptions = echarts.EChartsOption;

export interface CommonBarAndLineType {
  // 这个table是为了SubMenuCard组件能接收所有参数而做的妥协
  chartType: SymbolType;
  // chart的高度
  height?: number;
  // x轴数据
  xAxisData: string[];
  // 实例数据，每一条线
  seriesDataArray: Array<{
    data: Array<any>;
    seriesOptions?: EChartsOptions['series'];
    color: string;
    name: string;
  }>;
  legendIcon?: (name: string, color: string) => string;
  dateType: ServerTimeType;
  transTopic: (name: string) => string;
  indicators: IndicatorsType;
  sumMap: any;
  noShow?: boolean;
  title?: string;
}

export default function CommonBarAndLine({
  height = 300,
  xAxisData,
  seriesDataArray,
  chartType,
  noShow,
  dateType,
  transTopic,
  indicators,
  sumMap,
  title,
}: CommonBarAndLineType) {
  const { formatMessage } = useIntl();
  const dealwithValueFunc = dealwithValueFuncMap[indicators];
  const dealwithYFunc = dealwithEChartYFuncMap[indicators];
  // 设置图形配置
  const makeupOptions = (appointLegendSelected?: {
    [key: string]: boolean;
  }) => {
    const colorArr: Array<string> = [];
    const seriesData: any = [];
    const legendData: Array<any> = [];
    let legendSelected: any = {};
    seriesDataArray.forEach(
      ({ data, seriesOptions = {}, color, name }, index) => {
        if (name === 'Others') return;
        const seriesItem: any = {
          data,
          type: chartType as any,
          name,
          barMaxWidth: 80,
          itemStyle: {
            color: color || chartAllColors[index],
          },
          ...seriesOptions,
        };
        if (chartType === 'bar') {
          seriesItem['stack'] = 'Total';
          seriesItem['areaStyle'] = {
            color,
          };
        }

        seriesData.push(seriesItem);
        legendData.push({
          name,
          icon: 'circle',
          itemStyle: {
            color,
          },
        });
        colorArr.push(color);
        legendSelected[name] = index < 10;
      },
    );
    legendSelected = appointLegendSelected || legendSelected;
    const opts: EChartsOptions = {
      tooltip: {
        trigger: 'axis',
        axisPointer: chartType === 'line' ? {} : { type: 'shadow' },
        formatter: (params: Array<any>) => {
          const xAxisTitle = params?.[0].axisValue;
          const total = sumMap[xAxisTitle] || 0;
          return formatChartParamsDate({
            params,
            total,
            valueFormat: dealwithValueFunc,
            listTitleFunc: (item) => {
              if (getLocale() === 'zh-CN' && item.seriesName === 'Others') {
                return '其他';
              }
              return transTopic(item.seriesName);
            },
            dealwithRecords(records, total) {
              const newRecords = records
                .filter((item) => item.originValue !== 0)
                .map((item) => ({
                  ...item,
                  share: getPercentage(item.originValue || 0, total),
                }));
              return newRecords;
            },
            tablesConfigFunc: (records: Array<any>) => {
              const result: any = [
                {
                  showHeader: true,
                  records,
                  columns: [
                    {
                      key: 'text',
                      header: listTimeTitleFunc(xAxisTitle, dateType),
                    },
                    {
                      key: 'value',
                      align: 'right',
                      weight: 'bold',
                      header: formatMessage({
                        id: title || indicatorsMap[indicators],
                      }),
                    },
                  ],
                },
              ];
              if (!noShow) {
                result[0].columns.push({
                  showHeader: true,
                  key: 'share',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage({ id: 'Share-1' }),
                });
              }
              return result;
            },
            isSort: true,
          });
        },
        position: (pos, params, dom, rect, size) => {
          return getChartPosition(pos, params, dom, rect, size, 0, 30);
        },
      },
      grid: {
        bottom: '10%',
        top: '20px',
        right: 25,
        containLabel: true,
      },
      legend: {
        data: legendData,
        formatter: (name) => {
          if (getLocale() === 'zh-CN' && name === 'Others') {
            return '其他';
          }
          return transTopic(name);
        },
      },
      color: colorArr,
      axisPointer: getAxisPointer((data) => {
        return formatTimeXAxisText(data.value, dateType);
      }),
      xAxis: {
        data: xAxisData,
        boundaryGap: chartType === 'bar',
        axisLabel: {
          align: 'center',
          formatter: (value: any) => formatTimeXAxisText(value, dateType),
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value) => {
            const data = dealwithYFunc(Math.abs(value), 1);
            return value >= 0 ? data : `-${data}`;
          },
        },
      },
      series: seriesData,
    };

    return opts;
  };

  const options = useMemo(() => {
    return makeupOptions();
  }, [seriesDataArray, chartType, transTopic]);

  return (
    <EChartCommon
      optionsProps={{ type: chartType as 'line' | 'bar' }}
      options={options}
      height={height}
    ></EChartCommon>
  );
}
